import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Breadcrumb,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  message,
  TableProps,
} from 'antd';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import UnitTypeModel from './UnitTypeModel';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

interface UnitType {
  ut_id: number;
  ut_name: string;
  ut_status: number;
}

const UnitType: React.FC = () => {
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<UnitType | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const cancel: PopconfirmProps['onCancel'] = (e) => {
    showError('Action cancelled');
  };

  const fetchUnitTypes = async () => {
    try {
      const data = await apiRequest<UnitType[]>({
        method: 'get',
        url: 'unit-types', // Replace with your actual endpoint
      });
      setUnitTypes(data);
    } catch (error) {
      showError('Failed to fetch unit types');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchUnitTypes();
  }, []);

  const handleEditClick = (record: UnitType) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleCreate = () => {
    fetchUnitTypes();
  };

  const handleDelete = async (ut_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-unit-type/${ut_id}`, // Adjust API path accordingly
      });
      showSuccess('Unit type deleted');
      fetchUnitTypes();
    } catch {
      showError('Failed to delete unit type');
    }
  };

  const handleStatusToggle = async (ut_id: number) => {
    // Optimistic UI update
    setUnitTypes((prev) =>
      prev.map((item) =>
        item.ut_id === ut_id ? { ...item, ut_status: item.ut_status === 1 ? 0 : 1 } : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-unit-type/${ut_id}`, // Adjust API path accordingly
      });
      showSuccess('Unit type status updated');
    } catch (error) {
      showError('Failed to update status');
      // Revert UI update
      setUnitTypes((prev) =>
        prev.map((item) =>
          item.ut_id === ut_id ? { ...item, ut_status: item.ut_status === 1 ? 0 : 1 } : item
        )
      );
    }
  };

  const handleTableChange: TableProps<UnitType>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<UnitType> = [
    {
      title: 'Unit Type',
      dataIndex: 'ut_name',
      key: 'ut_name',
      filters: Array.from(new Set(unitTypes.map((s) => s.ut_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.ut_name || null,
      onFilter: (value, record) => record.ut_name.includes(value as string),
      sorter: (a, b) => a.ut_name.localeCompare(b.ut_name),
      sortOrder: sortedInfo.columnKey === 'ut_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.ut_name}</div>,
    },
  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      key: 'actions',
      render: (_: any, record: UnitType) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.ut_status}
                onChange={() => handleStatusToggle(record.ut_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this unit type?"
              onConfirm={() => handleDelete(record.ut_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Unit Types</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Button type="primary" onClick={showCreateModal}>
            Add Unit Type
          </Button>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<UnitType>
          tableLayout="auto"
          columns={columns}
          onChange={handleTableChange}
          rowKey="ut_id"
          dataSource={unitTypes.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No unit types found' }}
          footer={() => (
            <Space
              style={{ width: '100%', justifyContent: 'space-between', padding: '0 16px' }}
            >
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, unitTypes.length)} of{' '}
                {unitTypes.length}
              </span>
              <Pagination
                {...pagination}
                total={unitTypes.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Space>
          )}
        />
      </div>

      <UnitTypeModel
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default UnitType;
